import { EventDispatcher } from '../../events/EventDispatcher/EventDispatcher';
import { assertNotNullOrUndefined } from '../../utils/assert/assertNotNullOrUndefined';
import { Disposable } from '../../utils/DisposableContainer/DisposableContainer';

export class BindableValue<T> {
  private readonly eventEmitter = new EventDispatcher<{ valueChanged: T }>();
  private readonly noValueAvailableErrorMessage: string;
  private valueInfo: { value: T } | null = null;

  constructor(options?: {
    defaultValue?: T;
    noValueAvailableErrorMessage?: string;
  }) {
    this.noValueAvailableErrorMessage =
      options?.noValueAvailableErrorMessage ??
      "can't get a value since it has not been set yet";

    if (options && options.defaultValue !== undefined) {
      this.valueInfo = { value: options.defaultValue };
    }
  }

  /**
   * Only new instances will be accepted. If you pass a value, which is the same instance as the already set value, then nothing will happen.
   */
  public setValue(value: T): void {
    if (this.valueInfo && this.valueInfo.value === value) {
      return;
    }

    this.valueInfo = { value };
    this.eventEmitter.dispatchEvent('valueChanged', value);
  }

  public getValue(): T {
    assertNotNullOrUndefined(this.valueInfo, this.noValueAvailableErrorMessage);
    return this.valueInfo.value;
  }

  public bindValue(callback: (value: T) => void): Disposable {
    callback(this.getValue());
    return this.onValueChanged(callback);
  }

  public onValueChanged(callback: (value: T) => void): Disposable {
    return this.eventEmitter.addEventListener('valueChanged', callback);
  }

  public reset(): void {
    this.valueInfo = null;
  }
}
